<template>
  <section class="page-stack">
    <article class="card page-hero-card">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Mythic+ Runs</p>
          <h2>Dungeon progression</h2>
          <p>Top keys, team rosters, affix context and seasonal Raider.io cutoffs in one view.</p>
        </div>
      </header>
    </article>

    <div class="card filter-toolbar">
      <div class="applied-filters">
        <span class="filter-pill">Expansion: {{ expansionLabel }}</span>
        <span class="filter-pill">Season: {{ selectedSeason?.short_name || 'Current' }}</span>
        <span class="filter-pill">Region: {{ filters.region }}</span>
        <span class="filter-pill">Dungeon: {{ selectedDungeonLabel }}</span>
        <span class="filter-pill">Affixes: {{ affixesFilterLabel }}</span>
      </div>
      <div class="page-actions">
        <button class="btn-icon-outline" type="button" aria-label="Reset filters" title="Reset filters" @click="resetFilters">
          <i class="fa-solid fa-rotate-left"></i>
        </button>
        <button class="btn-icon" type="button" aria-label="Filters" title="Filters" @click="openFilters">
          <i class="fa-solid fa-sliders"></i>
        </button>
        <button class="btn-icon-outline" type="button" aria-label="Refresh" title="Refresh" @click="loadData">
          <i class="fa-solid fa-rotate"></i>
        </button>
        <a class="btn-icon-outline" :href="leaderboardUrl" target="_blank" rel="noreferrer" aria-label="Open leaderboard" title="Open leaderboard">
          <i class="fa-solid fa-arrow-up-right-from-square"></i>
        </a>
      </div>
    </div>

    <div
      v-if="isFiltersOpen"
      class="app-modal-backdrop filter-drawer-backdrop"
      :class="{ 'is-closing': closingFilters }"
      @click.self="closeFilters"
    >
      <aside class="app-filter-drawer" role="dialog" aria-modal="true" aria-labelledby="mythic-filters-title">
        <header>
          <h2 id="mythic-filters-title">Mythic+ filters</h2>
          <p>Season, region, dungeon and affix filters.</p>
        </header>
        <section>
          <form class="form filters-grid" @submit.prevent="applyFilters">
            <div role="group" class="field filter-field">
              <label class="label">Expansion</label>
              <select v-model.number="draftFilters.expansionId" class="select">
                <option :value="11">Midnight</option>
                <option :value="10">The War Within</option>
                <option :value="9">Dragonflight</option>
                <option :value="8">Shadowlands</option>
              </select>
            </div>

            <div role="group" class="field filter-field">
              <label class="label">Season</label>
              <select v-model="draftFilters.season" class="select">
                <option v-for="season in draftSeasonOptions" :key="season.slug" :value="season.slug">
                  {{ season.name }} · {{ season.short_name }}
                </option>
              </select>
            </div>

            <div role="group" class="field filter-field">
              <label class="label">Region</label>
              <select v-model="draftFilters.region" class="select">
                <option value="world">World</option>
                <option value="eu">EU</option>
                <option value="us">US</option>
                <option value="kr">KR</option>
                <option value="tw">TW</option>
                <option value="cn">CN</option>
              </select>
            </div>

            <div role="group" class="field filter-field">
              <label class="label">Dungeon</label>
              <select v-model="draftFilters.dungeon" class="select">
                <option value="all">All</option>
                <option v-for="dungeon in draftVisibleDungeons" :key="dungeon.slug" :value="dungeon.slug">
                  {{ dungeon.name }}
                </option>
              </select>
            </div>

            <div role="group" class="field filter-field">
              <label class="label">Affixes</label>
              <select v-model="draftFilters.affixes" class="select">
                <option value="all">All</option>
                <option value="current">Current week</option>
                <option value="tyrannical">Tyrannical</option>
                <option value="fortified">Fortified</option>
              </select>
            </div>

          </form>
        </section>
        <footer>
          <button class="btn-outline" type="button" @click="closeFilters">Cancel</button>
          <button class="btn" type="button" @click="applyFilters">Apply</button>
        </footer>
      </aside>
    </div>

    <div v-if="error" class="card"><section>{{ error }}</section></div>

    <!-- <div class="stats-grid">
      <article class="stat-card card"><section><span>Runs loaded</span><strong>{{ filteredRuns.length }}</strong></section></article>
      <article class="stat-card card"><section><span>Best level</span><strong>{{ topMythicLevel }}</strong></section></article>
      <article class="stat-card card"><section><span>Current affixes</span><strong><span class="affix-icon-list"><span v-for="affix in affixes" :key="affix.id" class="affix-icon" :title="affix.name"><img v-if="affixIcon(affix)" :src="affixIcon(affix)" :alt="affix.name" loading="lazy"></span></span></strong></section></article>
      <article class="stat-card card"><section><span>Cutoff</span><strong>{{ cutoffLabel }}</strong></section></article>
    </div> -->

    <article class="season-reference-panel">
      <section class="reference-stack compact-reference">
        <div class="cutoff-statistics">
          <div class="mini-list compact-cutoffs">
            <div v-for="item in animatedCutoffEntries" :key="item.label" class="mini-list-row">
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
            </div>
          </div>
        </div>
      </section>
    </article>

    <div class="content-grid mythic-runs-layout">
      <article class="card">
        <header class="section-head">
          <div><p class="eyebrow">Runs</p><h3>Top runs</h3></div>
          <div class="view-switch" aria-label="Display mode">
            <button
              class="btn-icon-outline"
              :class="{ 'is-active': viewMode === 'grid' }"
              type="button"
              aria-label="Cards"
              @click="viewMode = 'grid'"
            >
              <i class="fa-solid fa-grip"></i>
            </button>
            <button
              class="btn-icon-outline"
              :class="{ 'is-active': viewMode === 'table' }"
              type="button"
              aria-label="Table"
              @click="viewMode = 'table'"
            >
              <i class="fa-solid fa-table-list"></i>
            </button>
          </div>
        </header>

        <section v-if="showDungeonChart" class="dungeon-chart-panel">
          <header class="dungeon-chart-head">
            <div>
              <p class="eyebrow">Level distribution</p>
              <h4>Timed +{{ selectedChartLevel }} keys by dungeon</h4>
            </div>
            <div class="dungeon-chart-actions">
              <span>{{ maxLevelTeamTotal }} teams</span>
              <div v-if="chartLevelOptions.length > 1" class="view-switch" aria-label="Dungeon chart level">
                <button
                  v-for="level in chartLevelOptions"
                  :key="level"
                  class="btn-sm-outline"
                  :class="{ 'is-active': selectedChartLevel === level }"
                  type="button"
                  :aria-label="`Show level ${level} distribution`"
                  @click="setChartLevel(level)"
                >
                  +{{ level }}
                </button>
              </div>
            </div>
          </header>

          <div class="dungeon-chart" :key="`chart-${selectedChartLevel}`" :style="{ '--chart-max-count': chartMaxCount }">
            <div
              v-for="item in maxLevelDungeonStats"
              :key="`${selectedChartLevel}-${item.slug}`"
              class="dungeon-chart-item"
              :title="`${item.name}: ${item.count} teams`"
            >
              <span class="dungeon-chart-bar-wrap">
                <span class="dungeon-chart-value" :style="{ bottom: chartValueBottom(item.count) }">{{ item.count }}</span>
                <span class="dungeon-chart-bar" :style="{ height: chartBarHeight(item.count) }"></span>
              </span>
              <span class="dungeon-chart-label">{{ shortDungeonName(item.name) }}</span>
            </div>
          </div>
        </section>

        <section v-if="loading" class="skeleton-stack">
          <div v-for="slot in 3" :key="`run-skeleton-${slot}`" class="card skeleton-card">
            <div class="skeleton-media"></div>
            <div class="skeleton-line skeleton-line-medium"></div>
            <div class="skeleton-line"></div>
            <div class="skeleton-row">
              <span class="skeleton-avatar"></span>
              <span class="skeleton-line skeleton-line-short"></span>
            </div>
          </div>
        </section>

        <section v-else-if="viewMode === 'grid'" class="run-grid run-card-grid">
          <article v-for="entry in filteredRuns" :key="entry.run.keystone_run_id" class="dungeon-run-card">
            <a class="dungeon-run-media" :href="runUrl(entry)" target="_blank" rel="noreferrer">
              <img
                v-if="dungeonImage(entry.run.dungeon)"
                :src="dungeonImage(entry.run.dungeon)"
                :alt="entry.run.dungeon.name"
                loading="lazy"
              >
              <span class="run-level">{{ entry.run.mythic_level }}</span>
            </a>

            <section class="dungeon-run-body">
              <span class="faction-watermark" :class="`is-${entry.run.faction || 'mixed'}`">
                <img :src="factionBanner(entry.run.faction)" :alt="entry.run.faction || 'mixed faction'" loading="lazy">
              </span>

              <h4 class="dungeon-run-title">
                <span>#{{ entry.rank }}</span>
                <a :href="runUrl(entry)" target="_blank" rel="noreferrer">{{ entry.run.dungeon.name }}</a>
              </h4>

              <div class="run-summary-grid">
                <div>
                  <p>Affixes</p>
                  <div class="affix-list compact">
                    <a
                      v-for="affix in entry.run.weekly_modifiers"
                      :key="affix.id"
                      :href="affixUrl(affix)"
                      :title="affix.name"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img v-if="affixIcon(affix)" :src="affixIcon(affix)" :alt="affix.name" loading="lazy">
                    </a>
                  </div>
                </div>
                <div>
                  <p>Time</p>
                  <strong>{{ duration(entry.run.clear_time_ms) }} <span>({{ remainingTime(entry.run) }})</span></strong>
                </div>
                <div>
                  <p>Score</p>
                  <strong>{{ scoreValue(entry.score) }}</strong>
                </div>
              </div>

              <div class="run-team-block">
                <p>Team</p>
                <div class="run-team-grid">
                  <div v-for="member in entry.run.roster" :key="member.character.path" class="run-player-pill">
                    <span class="role-mark" :class="`is-${member.role}`">
                      <img :src="roleImage(member.role)" :alt="member.role" loading="lazy">
                    </span>
                    <a
                      class="player-name"
                      :style="{ color: classColor(member.character.class.slug) }"
                      :href="characterUrl(member.character.path)"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {{ characterName(member.character.name) }}
                    </a>
                    <span class="player-actions">
                      <button type="button" aria-label="Add to favorites" title="Add to favorites" @click="favoriteMember(member.character)">
                        <i class="fa-solid fa-heart"></i>
                      </button>
                      <button type="button" aria-label="Add to compare" title="Add to compare" @click="compareMember(member.character)">
                        <i class="fa-solid fa-scale-balanced"></i>
                      </button>
                    </span>
                  </div>
                </div>
              </div>
            </section>
          </article>
        </section>

        <section v-if="false" class="run-grid run-card-grid">
          <div v-for="entry in filteredRuns" :key="entry.run.keystone_run_id" class="card run-card media-card">
            <section v-if="dungeonImage(entry.run.dungeon)" class="media-section">
              <img
                class="media-image"
                :src="dungeonImage(entry.run.dungeon)"
                :alt="entry.run.dungeon.name"
                loading="lazy"
              >
            </section>
            <header class="run-card-header">
              <div>
                <h4>#{{ entry.rank }} · {{ entry.run.dungeon.name }}</h4>
                <p>+{{ entry.run.mythic_level }} · {{ score(entry.score) }} score · {{ formatDate(entry.run.completed_at) }}</p>
              </div>
              <a class="btn-sm-outline" :href="runUrl(entry)" target="_blank" rel="noreferrer" aria-label="Run details">
                <i class="fa-solid fa-arrow-up-right-from-square"></i>
              </a>
            </header>

            <section class="run-card-meta">
              <span>Timer: {{ duration(entry.run.clear_time_ms) }} / {{ duration(entry.run.keystone_time_ms) }}</span>
              <span>Upgrades: {{ entry.run.num_chests }}</span>
              <span>Faction: {{ entry.run.faction }}</span>
              <span>Team: {{ teamLabel(entry.run) }}</span>
            </section>

            <section class="chip-row">
              <span v-for="affix in entry.run.weekly_modifiers" :key="affix.id" class="affix-icon" :title="affix.name">
                <img v-if="affixIcon(affix)" :src="affixIcon(affix)" :alt="affix.name" loading="lazy">
              </span>
            </section>

            <section class="roster-grid">
              <article v-for="member in entry.run.roster" :key="member.character.path" class="card member-card">
                <section class="character-line">
                  <img
                    class="icon-image"
                    :src="classIcon(member.character.class.slug)"
                    :alt="member.character.class.name"
                    loading="lazy"
                  >
                  <div>
                    <strong>{{ member.character.name }}</strong>
                    <p>{{ member.character.class.name }} · {{ member.character.spec.name }} · {{ member.role }}</p>
                    <p class="muted-line">{{ member.character.region.short_name }} / {{ member.character.realm.name }}</p>
                  </div>
                </section>

                <footer class="member-actions">
                  <a class="btn-sm-outline" :href="characterUrl(member.character.path)" target="_blank" rel="noreferrer">
                    <i class="fa-solid fa-user"></i>
                  </a>
                  <button class="btn-sm" type="button" aria-label="Add to favorites" @click="favoriteMember(member.character)">
                    <i class="fa-solid fa-star"></i>
                  </button>
                </footer>
              </article>
            </section>
          </div>
        </section>

        <section v-if="!loading && viewMode === 'table'" class="table-wrap run-table-wrap">
          <table class="table dungeon-run-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Dungeon</th>
                <th>Level</th>
                <th>Time <span>(Remaining)</span></th>
                <th>Affixes</th>
                <th>Tank</th>
                <th>Healer</th>
                <th>DPS</th>
                <th>Score</th>
                <th>Faction</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in filteredRuns" :key="`table-${entry.run.keystone_run_id}`">
                <td class="rank-cell">{{ entry.rank }}</td>
                <td class="dungeon-cell">
                  <a :href="runUrl(entry)" target="_blank" rel="noreferrer">
                    <img v-if="dungeonIcon(entry.run.dungeon)" :src="dungeonIcon(entry.run.dungeon)" :alt="entry.run.dungeon.name" loading="lazy">
                    <span>{{ entry.run.dungeon.name }}</span>
                  </a>
                </td>
                <td>+{{ entry.run.mythic_level }}</td>
                <td class="time-cell">{{ duration(entry.run.clear_time_ms) }} <span>({{ remainingTime(entry.run) }})</span></td>
                <td>
                  <div class="affix-list">
                    <a
                      v-for="affix in entry.run.weekly_modifiers"
                      :key="affix.id"
                      :href="affixUrl(affix)"
                      :title="affix.name"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img v-if="affixIcon(affix)" :src="affixIcon(affix)" :alt="affix.name" loading="lazy">
                    </a>
                  </div>
                </td>
                <td class="role-cell">
                  <div v-for="member in rosterByRole(entry.run, 'tank')" :key="member.character.path" class="table-player">
                    <span class="role-mark is-tank"><img :src="roleImage('tank')" alt="tank" loading="lazy"></span>
                    <a :style="{ color: classColor(member.character.class.slug) }" :href="characterUrl(member.character.path)" target="_blank" rel="noreferrer">
                      {{ characterName(member.character.name) }}
                    </a>
                    <span class="table-player-popup">
                      <button type="button" aria-label="Add to favorites" title="Add to favorites" @click="favoriteMember(member.character)">
                        <i class="fa-solid fa-heart"></i>
                      </button>
                      <button type="button" aria-label="Add to compare" title="Add to compare" @click="compareMember(member.character)">
                        <i class="fa-solid fa-scale-balanced"></i>
                      </button>
                    </span>
                  </div>
                </td>
                <td class="role-cell">
                  <div v-for="member in rosterByRole(entry.run, 'healer')" :key="member.character.path" class="table-player">
                    <span class="role-mark is-healer"><img :src="roleImage('healer')" alt="healer" loading="lazy"></span>
                    <a :style="{ color: classColor(member.character.class.slug) }" :href="characterUrl(member.character.path)" target="_blank" rel="noreferrer">
                      {{ characterName(member.character.name) }}
                    </a>
                    <span class="table-player-popup">
                      <button type="button" aria-label="Add to favorites" title="Add to favorites" @click="favoriteMember(member.character)">
                        <i class="fa-solid fa-heart"></i>
                      </button>
                      <button type="button" aria-label="Add to compare" title="Add to compare" @click="compareMember(member.character)">
                        <i class="fa-solid fa-scale-balanced"></i>
                      </button>
                    </span>
                  </div>
                </td>
                <td class="role-cell dps-cell">
                  <div v-for="member in rosterByRole(entry.run, 'dps')" :key="member.character.path" class="table-player">
                    <span class="role-mark is-dps"><img :src="roleImage('dps')" alt="dps" loading="lazy"></span>
                    <a :style="{ color: classColor(member.character.class.slug) }" :href="characterUrl(member.character.path)" target="_blank" rel="noreferrer">
                      {{ characterName(member.character.name) }}
                    </a>
                    <span class="table-player-popup">
                      <button type="button" aria-label="Add to favorites" title="Add to favorites" @click="favoriteMember(member.character)">
                        <i class="fa-solid fa-heart"></i>
                      </button>
                      <button type="button" aria-label="Add to compare" title="Add to compare" @click="compareMember(member.character)">
                        <i class="fa-solid fa-scale-balanced"></i>
                      </button>
                    </span>
                  </div>
                </td>
                <td class="score-cell">{{ scoreValue(entry.score) }}</td>
                <td class="faction-cell">
                  <span :class="`faction-badge is-${entry.run.faction || 'mixed'}`">
                    <img :src="factionBanner(entry.run.faction)" :alt="entry.run.faction || 'mixed faction'" loading="lazy">
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <nav v-if="!loading && filteredRuns.length" class="runs-pagination" aria-label="Runs pagination">
          <button
            class="pagination-btn"
            type="button"
            :disabled="filters.page <= 0"
            aria-label="Previous page"
            @click="setRunsPage(filters.page - 1)"
          >
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <button
            v-for="page in paginationPages"
            :key="page"
            class="pagination-btn"
            :class="{ 'is-active': filters.page === page }"
            type="button"
            @click="setRunsPage(page)"
          >
            {{ page + 1 }}
          </button>
          <button
            class="pagination-btn"
            type="button"
            aria-label="Next page"
            @click="setRunsPage(filters.page + 1)"
          >
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        </nav>

        <section v-if="false" class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Dungeon</th>
                <th>Level</th>
                <th>Score</th>
                <th>Time</th>
                <th>Team</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in filteredRuns" :key="`table-${entry.run.keystone_run_id}`">
                <td>{{ entry.rank }}</td>
                <td>
                  <div class="table-title">
                    <img v-if="dungeonImage(entry.run.dungeon)" :src="dungeonImage(entry.run.dungeon)" :alt="entry.run.dungeon.name" loading="lazy">
                    <span>{{ entry.run.dungeon.name }}</span>
                  </div>
                </td>
                <td>+{{ entry.run.mythic_level }}</td>
                <td>{{ score(entry.score) }}</td>
                <td>{{ duration(entry.run.clear_time_ms) }}</td>
                <td>{{ teamLabel(entry.run) }}</td>
                <td>
                  <a class="btn-sm-outline" :href="runUrl(entry)" target="_blank" rel="noreferrer" aria-label="Run details">
                    <i class="fa-solid fa-arrow-up-right-from-square"></i>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      </article>

    </div>
  </section>
</template>

<script>
import { getAffixes, getCharacterProfile, getLeaderboardCapacity, getMythicPlusRuns, getMythicPlusStaticData, getSeasonCutoffs } from '../services/raiderApi'
import { formatDate, formatDuration, formatScore } from '../utils/formatters'
import allianceBanner from '../assets/images/alliance-banner.svg'
import allianceHordeBanner from '../assets/images/alliance-horde-banner.svg'
import hordeBanner from '../assets/images/horde-banner.svg'
import dpsRoleIcon from '../assets/images/roles/role_dps.png'
import healerRoleIcon from '../assets/images/roles/role_healer.png'
import tankRoleIcon from '../assets/images/roles/role_tank.png'

const CLASS_COLORS = {
  deathknight: '#c41e3a',
  'death-knight': '#c41e3a',
  demonhunter: '#a330c9',
  'demon-hunter': '#a330c9',
  druid: '#ff7c0a',
  evoker: '#33937f',
  hunter: '#aad372',
  mage: '#3fc7eb',
  monk: '#00ff98',
  paladin: '#f48cba',
  priest: '#ffffff',
  rogue: '#fff468',
  shaman: '#0070dd',
  warlock: '#8788ee',
  warrior: '#c69b6d',
}

const DEFAULT_MYTHIC_PLUS_STATE = {
  viewMode: 'grid',
  filters: {
    expansionId: 11,
    season: '',
    region: 'world',
    dungeon: 'all',
    affixes: 'all',
    page: 0,
  },
}

const CHART_RUN_PAGE_COUNT = 10
const MYTHIC_PLUS_STATE_KEY = 'rio-dashboard-mythic-plus-state'

const ROLE_IMAGES = {
  tank: tankRoleIcon,
  healer: healerRoleIcon,
  dps: dpsRoleIcon,
}

const FACTION_BANNERS = {
  alliance: allianceBanner,
  horde: hordeBanner,
  mixed: allianceHordeBanner,
}

const dungeonImageContext = require.context('../assets/images/dungeons', false, /\.(jpg|jpeg|png|webp)$/)
const LOCAL_DUNGEON_IMAGES = dungeonImageContext.keys().reduce((images, key) => {
  const slug = key.replace('./', '').replace(/\.(jpg|jpeg|png|webp)$/i, '')
  images[slug] = dungeonImageContext(key)
  return images
}, {})

function loadMythicPlusState() {
  try {
    const saved = JSON.parse(localStorage.getItem(MYTHIC_PLUS_STATE_KEY) || '{}')
    const savedFilters = saved.filters && typeof saved.filters === 'object' ? saved.filters : {}
    const viewMode = ['grid', 'table'].includes(saved.viewMode) ? saved.viewMode : DEFAULT_MYTHIC_PLUS_STATE.viewMode

    return {
      viewMode,
      filters: {
        ...DEFAULT_MYTHIC_PLUS_STATE.filters,
        ...savedFilters,
        expansionId: Number(savedFilters.expansionId || DEFAULT_MYTHIC_PLUS_STATE.filters.expansionId),
        page: Number(savedFilters.page || DEFAULT_MYTHIC_PLUS_STATE.filters.page),
      },
    }
  } catch (error) {
    return {
      ...DEFAULT_MYTHIC_PLUS_STATE,
      filters: { ...DEFAULT_MYTHIC_PLUS_STATE.filters },
    }
  }
}

export default {
  name: 'MythicPlusView',
  data() {
    const savedState = loadMythicPlusState()

    return {
      loading: false,
      isFiltersOpen: false,
      closingFilters: false,
      error: '',
      seasons: [],
      dungeons: [],
      runs: [],
      chartRuns: [],
      affixes: [],
      cutoffs: null,
      capacity: null,
      selectedChartLevelFilter: null,
      loadedExpansionId: null,
      viewMode: savedState.viewMode,
      filters: savedState.filters,
      draftFilters: { ...savedState.filters },
      draftSeasons: [],
      draftDungeons: [],
      loadedDraftExpansionId: null,
      animatedCutoffs: {},
      cutoffAnimationInterval: null,
    }
  },
  computed: {
    seasonOptions() {
      return this.seasons
        .filter((season) => season.is_main_season)
        .sort((left, right) => new Date(right.starts?.us || 0) - new Date(left.starts?.us || 0))
    },
    selectedSeason() {
      return this.seasons.find((season) => season.slug === this.filters.season) || null
    },
    draftSeasonOptions() {
      return this.draftSeasons
        .filter((season) => season.is_main_season)
        .sort((left, right) => new Date(right.starts?.us || 0) - new Date(left.starts?.us || 0))
    },
    draftSelectedSeason() {
      return this.draftSeasons.find((season) => season.slug === this.draftFilters.season) || null
    },
    visibleDungeons() {
      return this.selectedSeason?.dungeons?.length ? this.selectedSeason.dungeons : this.dungeons
    },
    draftVisibleDungeons() {
      return this.draftSelectedSeason?.dungeons?.length ? this.draftSelectedSeason.dungeons : this.draftDungeons
    },
    dungeonMap() {
      return this.dungeons.reduce((map, dungeon) => {
        map[dungeon.slug] = dungeon
        return map
      }, {})
    },
    expansionLabel() {
      return this.expansionName(this.filters.expansionId)
    },
    selectedDungeonLabel() {
      if (this.filters.dungeon === 'all') {
        return 'All'
      }
      return this.visibleDungeons.find((dungeon) => dungeon.slug === this.filters.dungeon)?.name || this.filters.dungeon
    },
    affixesFilterLabel() {
      const labels = {
        all: 'All',
        current: 'Current week',
        tyrannical: 'Tyrannical',
        fortified: 'Fortified',
      }
      return labels[this.filters.affixes] || this.filters.affixes
    },
    filteredRuns() {
      return this.runs
    },
    maxCompletedLevel() {
      return this.chartRuns.reduce((level, entry) => Math.max(level, Number(entry.run?.mythic_level || 0)), 0)
    },
    chartLevelOptions() {
      return [...new Set(this.chartRuns
        .map((entry) => Number(entry.run?.mythic_level || 0))
        .filter(Boolean))]
        .sort((left, right) => right - left)
        .slice(0, 3)
    },
    selectedChartLevel() {
      return this.chartLevelOptions.includes(this.selectedChartLevelFilter)
        ? this.selectedChartLevelFilter
        : this.chartLevelOptions[0] || 0
    },
    showDungeonChart() {
      return this.filters.dungeon === 'all' && this.maxLevelDungeonStats.length > 1
    },
    maxLevelDungeonStats() {
      if (!this.selectedChartLevel) {
        return []
      }

      const dungeons = this.chartRuns
        .filter((entry) => Number(entry.run?.mythic_level || 0) === this.selectedChartLevel)
        .reduce((map, entry) => {
          const dungeon = entry.run.dungeon || {}
          const slug = dungeon.slug || dungeon.short_name || dungeon.name
          if (!slug) {
            return map
          }

          if (!map[slug]) {
            map[slug] = {
              slug,
              name: dungeon.name || slug,
              teams: new Set(),
            }
          }

          map[slug].teams.add(entry.run.keystone_team_id || entry.run.keystone_run_id || `${entry.rank}-${slug}`)
          return map
        }, {})

      return Object.values(dungeons)
        .map((item) => ({
          slug: item.slug,
          name: item.name,
          count: item.teams.size,
        }))
        .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name))
    },
    chartMaxCount() {
      return Math.max(...this.maxLevelDungeonStats.map((item) => item.count), 1)
    },
    maxLevelTeamTotal() {
      return this.maxLevelDungeonStats.reduce((total, item) => total + item.count, 0)
    },
    topMythicLevel() {
      return this.filteredRuns[0]?.run?.mythic_level || '—'
    },
    affixLabel() {
      return this.affixes.length ? this.affixes.map((item) => item.name).join(', ') : '—'
    },
    cutoffEntries() {
      const cutoffs = this.cutoffs?.cutoffs || {}
      return [
        { label: 'Top 0.1%', target: this.cutoffTarget(cutoffs.p999?.all?.quantileMinValue) },
        { label: 'Top 1%', target: this.cutoffTarget(cutoffs.p990?.all?.quantileMinValue) },
        { label: 'Top 10%', target: this.cutoffTarget(cutoffs.p900?.all?.quantileMinValue) },
        { label: 'Keystone Legend', target: this.cutoffTarget(cutoffs.keystoneLegend?.score) },
      ]
    },
    animatedCutoffEntries() {
      return this.cutoffEntries.map((item) => ({
        label: item.label,
        value: this.loading ? '-' : this.cutoffDisplayValue(item),
      }))
    },
    cutoffLabel() {
      return this.cutoffEntries[0]?.target !== null ? Math.round(this.cutoffEntries[0].target).toString() : '—'
    },
    capacitySummary() {
      const first = this.capacity?.entries?.[0]
      if (!first) {
        return 'No leaderboard capacity data for the selected region.'
      }
      return `Minimum listed key: +${first.mythic_level} · ${first.dungeon?.name || 'Dungeon'} · ${formatDuration(first.clear_time_ms)}`
    },
    leaderboardUrl() {
      return `https://raider.io/mythic-plus-rankings/${this.filters.season}/${this.filters.dungeon}/${this.filters.region}/leaderboards-strict`
    },
    paginationPages() {
      const currentPage = Number(this.filters.page) || 0
      const startPage = Math.max(0, currentPage - 2)
      return Array.from({ length: 5 }, (_, index) => startPage + index)
    },
  },
  methods: {
    addFavorite(character, source = 'manual') {
      this.$store.dispatch('addFavorite', { character, source })
    },
    openFilters() {
      this.syncDraftFilters()
      this.closingFilters = false
      this.isFiltersOpen = true
      document.body.classList.add('is-scroll-locked')
    },
    closeFilters() {
      if (!this.isFiltersOpen || this.closingFilters) return
      this.closingFilters = true
      window.setTimeout(() => {
        this.isFiltersOpen = false
        this.closingFilters = false
        document.body.classList.remove('is-scroll-locked')
      }, 180)
    },
    applyFilters() {
      this.filters = {
        ...this.filters,
        ...this.draftFilters,
        page: 0,
      }
      this.closeFilters()
      this.loadData()
    },
    resetFilters() {
      this.filters = { ...DEFAULT_MYTHIC_PLUS_STATE.filters }
      this.draftFilters = { ...DEFAULT_MYTHIC_PLUS_STATE.filters }
      this.viewMode = DEFAULT_MYTHIC_PLUS_STATE.viewMode
      localStorage.removeItem(MYTHIC_PLUS_STATE_KEY)
      this.loadData()
    },
    syncDraftFilters() {
      this.draftFilters = { ...this.filters }
      this.ensureDraftStaticData()
    },
    expansionName(expansionId) {
      const names = {
        11: 'Midnight',
        10: 'The War Within',
        9: 'Dragonflight',
        8: 'Shadowlands',
      }
      return names[expansionId] || `Expansion ${expansionId}`
    },
    setRunsPage(page) {
      const nextPage = Math.max(0, Number(page) || 0)
      if (nextPage === this.filters.page) return
      this.filters.page = nextPage
      this.loadData()
    },
    setChartLevel(level) {
      this.selectedChartLevelFilter = Number(level) || null
    },
    chartBarHeight(count) {
      const percent = Math.max(8, Math.round((Number(count || 0) / this.chartMaxCount) * 100))
      return `${percent}%`
    },
    chartValueBottom(count) {
      const percent = Math.max(8, Math.round((Number(count || 0) / this.chartMaxCount) * 100))
      return `calc(${percent}% + 0.35rem)`
    },
    startCutoffAnimation() {
      this.stopCutoffAnimation()
      const targets = this.cutoffEntries.reduce((map, item) => {
        map[item.label] = item.target
        return map
      }, {})
      this.animatedCutoffs = Object.fromEntries(
        Object.keys(targets).map((label) => [label, targets[label] === null ? null : 0])
      )

      const duration = 400
      const stepMs = 30
      let elapsed = 0

      this.cutoffAnimationInterval = setInterval(() => {
        elapsed += stepMs
        const progress = Math.min(elapsed / duration, 1)
        this.animatedCutoffs = Object.fromEntries(
          Object.entries(targets).map(([label, target]) => [
            label,
            target === null ? null : Math.floor(target * progress),
          ])
        )

        if (progress < 1) {
          return
        }

        this.animatedCutoffs = { ...targets }
        this.stopCutoffAnimation()
      }, stepMs)
    },
    stopCutoffAnimation() {
      if (this.cutoffAnimationInterval) {
        clearInterval(this.cutoffAnimationInterval)
        this.cutoffAnimationInterval = null
      }
    },
    cutoffDisplayValue(item) {
      const animatedValue = this.animatedCutoffs[item.label]
      if (animatedValue !== undefined) {
        return animatedValue === null ? '—' : Math.round(animatedValue).toString()
      }

      return item.target === null ? '—' : Math.round(item.target).toString()
    },
    shortDungeonName(name) {
      return String(name || '')
        .replace(/^Operation:\s*/i, 'Op. ')
        .replace(/Tazavesh:\s*/i, 'Taz. ')
        .replace(/Streets of Wonder/i, 'Streets')
        .replace(/Soleah's Gambit/i, 'Gambit')
        .replace(/Eco-Dome Al'dani/i, 'Eco-Dome')
    },
    setSeasonLabel(label) {
      this.$store.dispatch('setSeasonLabel', label)
    },
    async loadData() {
      this.loading = true
      this.error = ''
      this.animatedCutoffs = {}
      this.stopCutoffAnimation()

      try {
        await this.ensureStaticData()

        const { expansionId, ...runFilters } = this.filters
        const regionForExtras = this.filters.region === 'world' ? 'eu' : this.filters.region
        const currentPage = Number(runFilters.page || 0)
        const shouldLoadChartRuns = runFilters.dungeon === 'all'
        const chartPages = shouldLoadChartRuns ? Array.from({ length: CHART_RUN_PAGE_COUNT }, (_, page) => page) : []
        const chartPageRequests = chartPages
          .filter((page) => page !== currentPage)
          .map((page) => getMythicPlusRuns({ ...runFilters, page }).catch(() => ({ rankings: [] })))

        const [runsData, chartRunsData, affixesData, cutoffsData, capacityData] = await Promise.all([
          getMythicPlusRuns(runFilters),
          Promise.all(chartPageRequests),
          getAffixes(regionForExtras).catch(() => ({})),
          getSeasonCutoffs(regionForExtras, this.filters.season).catch(() => ({})),
          getLeaderboardCapacity(regionForExtras).catch(() => ({})),
        ])

        this.runs = runsData.rankings || []
        this.chartRuns = shouldLoadChartRuns
          ? [
              ...(currentPage >= 0 && currentPage < CHART_RUN_PAGE_COUNT ? this.runs : []),
              ...chartRunsData.flatMap((pageData) => pageData.rankings || []),
            ]
          : []
        if (!this.chartLevelOptions.includes(this.selectedChartLevelFilter)) {
          this.selectedChartLevelFilter = null
        }
        this.affixes = affixesData.affix_details || affixesData.affixes || []
        this.cutoffs = cutoffsData
        this.capacity = capacityData

        const season = this.seasons.find((item) => item.slug === this.filters.season)
        this.setSeasonLabel(season ? season.short_name : 'Current Raider.io season')
      } catch (error) {
        this.error = 'Unable to load Mythic+ data. Try another region or page.'
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Mythic+ load failed',
          description: this.error,
        })
      } finally {
        this.loading = false
        this.startCutoffAnimation()
      }
    },
    async ensureStaticData() {
      if (this.loadedExpansionId !== this.filters.expansionId || !this.seasons.length) {
        const staticData = await getMythicPlusStaticData(this.filters.expansionId)
        this.seasons = staticData.seasons || []
        this.dungeons = staticData.dungeons || []
        this.loadedExpansionId = this.filters.expansionId
      }
      this.normalizeFilters('filters', this.seasons, this.dungeons)
    },
    async ensureDraftStaticData() {
      if (this.loadedDraftExpansionId !== this.draftFilters.expansionId || !this.draftSeasons.length) {
        const staticData = await getMythicPlusStaticData(this.draftFilters.expansionId)
        this.draftSeasons = staticData.seasons || []
        this.draftDungeons = staticData.dungeons || []
        this.loadedDraftExpansionId = this.draftFilters.expansionId
      }
      this.normalizeFilters('draftFilters', this.draftSeasons, this.draftDungeons)
    },
    normalizeFilters(targetKey, seasons, dungeons) {
      const target = this[targetKey]
      if (!seasons.some((season) => season.slug === target.season)) {
        target.season = this.pickDefaultSeason(seasons)
      }

      const selectedSeason = seasons.find((season) => season.slug === target.season) || null
      const visibleDungeons = selectedSeason?.dungeons?.length ? selectedSeason.dungeons : dungeons
      if (!visibleDungeons.some((dungeon) => dungeon.slug === target.dungeon)) {
        target.dungeon = 'all'
      }
    },
    pickDefaultSeason(seasons = this.seasons) {
      const seasonOptions = seasons
        .filter((season) => season.is_main_season)
        .sort((left, right) => new Date(right.starts?.us || 0) - new Date(left.starts?.us || 0))
      const now = Date.now()
      const activeSeason = seasonOptions.find((season) => {
        const start = new Date(season.starts?.us || season.starts?.eu || 0).getTime()
        const end = new Date(season.ends?.us || season.ends?.eu || 0).getTime()
        return start <= now && now <= end
      })

      return activeSeason?.slug || seasonOptions[0]?.slug || ''
    },
    async favoriteMember(character) {
      try {
        const profile = await getCharacterProfile({
          region: character.region.slug,
          realm: character.realm.slug,
          name: character.name,
          fields: [
            'gear',
            'guild',
            'raid_progression:current-expansion',
            'mythic_plus_scores_by_season:current',
            'mythic_plus_best_runs:all',
            'mythic_plus_recent_runs',
            'mythic_plus_ranks',
          ].join(','),
        })
        this.addFavorite(profile, 'mythic_plus')
      } catch (error) {
        this.error = `Unable to add ${character.name} to favorites.`
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Favorite failed',
          description: this.error,
        })
      }
    },
    async compareMember(character) {
      try {
        const profile = await this.fetchCharacterProfile(character)
        this.$store.dispatch('addToCompare', { profile, source: 'mythic_plus' })
      } catch (error) {
        this.error = `Unable to add ${character.name} to compare.`
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Compare failed',
          description: this.error,
        })
      }
    },
    fetchCharacterProfile(character) {
      return getCharacterProfile({
        region: character.region.slug,
        realm: character.realm.slug,
        name: character.name,
        fields: [
          'gear',
          'guild',
          'raid_progression:current-expansion',
          'mythic_plus_scores_by_season:current',
          'mythic_plus_best_runs:all',
          'mythic_plus_recent_runs',
          'mythic_plus_ranks',
        ].join(','),
      })
    },
    scoreValue(value) {
      if (value === null || value === undefined) return '-'
      return Math.round(Number(value)).toString()
    },
    cutoffScore(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return '-'
      return Math.round(Number(value)).toString()
    },
    cutoffTarget(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return null
      return Math.round(Number(value))
    },
    remainingTime(run) {
      const remaining = run.time_remaining_ms ?? (run.keystone_time_ms - run.clear_time_ms)
      return formatDuration(Math.max(remaining || 0, 0))
    },
    rosterByRole(run, role) {
      return run.roster.filter((member) => member.role === role)
    },
    characterName(name) {
      return (name || '').split('-')[0]
    },
    classColor(classSlug) {
      if (this.$store.state.themeMode !== 'dark') {
        return 'var(--foreground)'
      }
      return CLASS_COLORS[classSlug] || 'var(--foreground)'
    },
    roleImage(role) {
      return ROLE_IMAGES[role] || ROLE_IMAGES.dps
    },
    factionBanner(faction) {
      return FACTION_BANNERS[faction] || FACTION_BANNERS.mixed
    },
    persistMythicPlusState() {
      localStorage.setItem(MYTHIC_PLUS_STATE_KEY, JSON.stringify({
        viewMode: this.viewMode,
        filters: this.filters,
      }))
    },
    affixUrl(affix) {
      return `https://www.wowhead.com/affix=${affix.id}`
    },
    score(value) { return formatScore(value) },
    duration(value) { return formatDuration(value) },
    formatDate(value) { return formatDate(value) },
    characterUrl(path) { return `https://raider.io${path}` },
    normalizeAssetUrl(url) {
      if (!url) return ''
      return url.startsWith('http') ? url : `https://cdn.raiderio.net${url}`
    },
    dungeonImage(dungeon) {
      const staticDungeon = this.dungeonMap[dungeon.slug] || dungeon
      const imageUrl = staticDungeon.background_image_url || ''
      if (imageUrl && !imageUrl.includes('/icons/')) {
        return this.normalizeAssetUrl(imageUrl)
      }

      return LOCAL_DUNGEON_IMAGES[dungeon.slug] || this.normalizeAssetUrl(staticDungeon.icon_url || dungeon.icon_url)
    },
    dungeonIcon(dungeon) {
      const staticDungeon = this.dungeonMap[dungeon.slug] || dungeon
      return this.normalizeAssetUrl(staticDungeon.icon_url || staticDungeon.background_image_url)
    },
    affixIcon(affix) {
      return affix.icon ? `https://cdn.raiderio.net/images/wow/icons/large/${affix.icon}.jpg` : ''
    },
    classIcon(classSlug) {
      return `https://cdn.raiderio.net/images/wow/icons/large/classicon_${classSlug}.jpg`
    },
    runUrl(entry) { return `https://raider.io/mythic-plus-runs/${entry.run.season}/${entry.run.keystone_run_id}-${entry.run.mythic_level}-${entry.run.dungeon.slug}` },
    teamLabel(run) {
      const leadNames = run.roster.slice(0, 3).map((member) => member.character.name)
      return `#${run.keystone_team_id || run.keystone_run_id} · ${leadNames.join(', ')}`
    },
  },
  mounted() {
    this.loadData()
  },
  beforeUnmount() {
    this.stopCutoffAnimation()
  },
  watch: {
    viewMode() {
      this.persistMythicPlusState()
    },
    filters: {
      handler() {
        this.persistMythicPlusState()
      },
      deep: true,
    },
    'draftFilters.expansionId'() {
      this.draftFilters.season = ''
      this.draftFilters.dungeon = 'all'
      this.ensureDraftStaticData()
    },
  },
}
</script>
